import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Company, UserRole } from '@/lib/types';
import { useToast } from '@/components/ui/use-toast';

export const useCompanyState = (userId: string | undefined) => {
  const [activeCompany, setActiveCompanyState] = useState<Company | null>(null);
  const [hasPendingCompanyRequest, setHasPendingCompanyRequest] = useState(false);
  const [pendingCompany, setPendingCompany] = useState<{ id: string; name: string } | null>(null);
  const [userRole, setUserRole] = useState<UserRole | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    if (userId) {
      fetchActiveCompany(userId);
      checkCompanyRequest(userId);
      fetchUserRole(userId);
    }
  }, [userId]);

  // Fetches user role from profiles table
  // Note: Roles are stored in user_roles table but synced to profiles.role for convenience
  // Always use admin_assign_role RPC function to modify roles, never update profiles.role directly
  const fetchUserRole = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', userId)
        .single();
      
      if (error) throw error;
      if (data?.role) {
        setUserRole(data.role as UserRole);
      }
    } catch (error) {
      console.error('Error fetching user role:', error);
    }
  };

  const fetchActiveCompany = async (userId: string) => {
    try {
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('id', userId)
        .single();
      
      if (profileError) throw profileError;
      
      if (profileData?.company_id) {
        const { data: companyData, error: companyError } = await supabase
          .from('companies')
          .select('*')
          .eq('id', profileData.company_id)
          .single();
        
        if (companyError) throw companyError;
        setActiveCompanyState(companyData);
      }
    } catch (error) {
      console.error('Error fetching active company:', error);
    }
  };

  const checkCompanyRequest = async (userId: string) => {
    try {
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('requested_company_id')
        .eq('id', userId)
        .single();

      if (profileError) {
        console.error('Error fetching profile for company request:', profileError);
        return;
      }

      if (profileData.requested_company_id) {
        const { data: companyData, error: companyError } = await supabase
          .from('companies')
          .select('id, name')
          .eq('id', profileData.requested_company_id)
          .single();

        if (companyError) {
          console.error('Error fetching requested company:', companyError);
          return;
        }

        setHasPendingCompanyRequest(true);
        setPendingCompany({
          id: companyData.id,
          name: companyData.name
        });
      }
    } catch (error) {
      console.error('Error checking company request:', error);
    }
  };

  const setActiveCompany = async (companyId: string): Promise<boolean> => {
    try {
      if (!userId) return false;
      
      // Update company_id without touching the role
      // Roles should only be changed via admin_assign_role RPC function
      const { error } = await supabase
        .from('profiles')
        .update({ 
          company_id: companyId
        })
        .eq('id', userId);
      
      if (error) throw error;
      
      // After success, refresh the active company data
      await fetchActiveCompany(userId);
      
      toast({
        title: "Company Changed",
        description: `You are now working in ${activeCompany?.name || 'the selected company'}.`
      });
      
      return true;
    } catch (error: any) {
      console.error('Error setting company:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "An unexpected error occurred while switching companies."
      });
      return false;
    }
  };

  return {
    activeCompany,
    hasPendingCompanyRequest,
    pendingCompany,
    setActiveCompany,
    fetchActiveCompany
  };
};
